import { useCallback, useEffect, useRef, useState } from "react";
import { FileWarning, Send, RotateCcw, Paperclip } from "lucide-react";
import { http, streamSSE } from "@/lib/api";
import { useGroup } from "@/lib/GroupContext";

const stamp = (iso, origin) => {
  if (!iso || !origin) return "T+00:00:00";
  const s = Math.max(0, Math.floor((new Date(iso) - new Date(origin)) / 1000));
  const p = (n) => String(n).padStart(2, "0");
  return `T+${p(Math.floor(s / 3600))}:${p(Math.floor((s % 3600) / 60))}:${p(s % 60)}`;
};

function Entry({ m, suspect, origin, live }) {
  if (m.role === "exhibit") {
    return (
      <div className="exhibit-entry rise" data-testid="transcript-exhibit">
        <div className="flex items-center gap-3 font-mono text-sm tracking-[0.14em] font-semibold">
          <Paperclip size={18} /> EXHIBIT PRESENTED <span className="ml-auto opacity-60">{stamp(m.at, origin)}</span>
        </div>
        <div className="text-2xl font-black mt-2">{m.text}</div>
        <div className="text-[#505a5f] mt-1">Placed on the table in front of {suspect.name}.</div>
      </div>
    );
  }
  const isSus = m.role === "suspect";
  return (
    <div className={`record-entry ${live ? "" : "rise"}`} data-testid={isSus ? "transcript-suspect-line" : "transcript-investigator-line"}>
      <div>
        <div className={`font-mono text-[14px] tracking-[0.12em] font-semibold ${isSus ? "text-[#0033A1]" : "text-[#505a5f]"}`}>
          {isSus ? suspect.name.toUpperCase() : "INVESTIGATORS"}
        </div>
        <div className="font-mono text-xs text-[#505a5f] mt-1">{stamp(m.at, origin)}</div>
      </div>
      <p className={`text-[20px] leading-relaxed ${isSus ? "" : "text-[#383f43]"} ${live ? "caret" : ""}`}>{m.text}</p>
    </div>
  );
}

export function Interrogation({ suspect }) {
  const { state, setState } = useGroup();
  const [chat, setChat] = useState(null);
  const [input, setInput] = useState("");
  const [pending, setPending] = useState(null);
  const [live, setLive] = useState(null);
  const [error, setError] = useState(null);
  const logRef = useRef(null);
  const sid = suspect.id;
  const sus = state.suspects[sid];

  const load = useCallback(async () => {
    const { data } = await http.get(`/case/chats/${sid}`);
    setChat(data);
  }, [sid]);

  useEffect(() => {
    load();
    const t = setInterval(() => { if (!pending) load(); }, 8000);
    return () => clearInterval(t);
  }, [load, pending]);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [chat, live, pending]);

  const run = async (kind, text) => {
    setError(null);
    setPending({ kind, text });
    setLive("");
    let failed = false;
    try {
      await streamSSE(kind === "ask" ? `/case/chats/${sid}/ask` : `/case/chats/${sid}/present`,
        kind === "ask" ? { text } : null,
        (ev) => {
          if (ev.type === "delta") setLive((p) => (p || "") + ev.text);
          if (ev.type === "done") setState(ev.state);
          if (ev.type === "error") failed = true;
        });
    } catch (e) {
      failed = true;
    }
    await load();
    setLive(null);
    setPending(null);
    if (failed) {
      setError({ kind, text });
      if (kind === "ask") setInput(text);
    }
  };

  const send = (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || pending) return;
    setInput("");
    run("ask", text);
  };

  const messages = chat ? chat.messages : [];
  const origin = messages.length ? messages[0].at : null;
  const presented = messages.some((m) => m.role === "exhibit");
  const canPresent = sus.progress.clue && !sus.progress.secret && !pending;

  return (
    <div data-testid={`interrogation-${sid}`}>
      <h2 className="text-3xl font-black">Interview record</h2>
      <p className="text-lg text-[#505a5f] mt-2 mb-6">Put your questions to {suspect.name}. When you hold their hidden clue, present it as an exhibit.</p>
      <div className="record">
        <div className="record-head flex items-center gap-6 px-7 py-4">
          <span className="flex items-center gap-2 text-[#ff8a7a] font-semibold"><span className="w-2.5 h-2.5 rounded-full bg-[#ff5a45] rec-dot" /> REC</span>
          <span>INTERVIEW RECORD · MP/{suspect.initials}-SPORE</span>
          <span className="opacity-75">SUBJECT: {suspect.name.toUpperCase()}</span>
          <span className="ml-auto opacity-75" data-testid="interrogation-entry-count">{messages.length} ENTRIES</span>
        </div>
        <div ref={logRef} className="record-log max-h-[56vh] min-h-[320px] overflow-y-auto thin-scroll px-7 py-3" data-testid="interrogation-transcript">
          {messages.length === 0 && !pending && (
            <p className="text-[#505a5f] italic text-lg py-10 text-center">The tape is running. {suspect.name} is waiting for your first question.</p>
          )}
          {messages.map((m) => <div key={m.id} className={m.role === "exhibit" ? "my-4" : ""}><Entry m={m} suspect={suspect} origin={origin} /></div>)}
          {pending && pending.kind === "ask" && <Entry m={{ role: "investigators", text: pending.text, at: new Date().toISOString() }} suspect={suspect} origin={origin} />}
          {pending && live !== null && (
            <Entry m={{ role: "suspect", text: live || "…", at: new Date().toISOString() }} suspect={suspect} origin={origin} live />
          )}
          {error && (
            <div className="my-4 border-[5px] border-[#d4351c] bg-white px-5 py-4 flex items-center gap-4" data-testid="interrogation-error">
              <span className="error-msg text-lg">The suspect stares at you in silence. Try again.</span>
              <button className="btn-ghost ml-auto" onClick={() => (error.kind === "present" ? run("present") : (setInput(""), run("ask", error.text)))} data-testid="interrogation-retry-button">
                <RotateCcw size={16} /> Retry
              </button>
            </div>
          )}
        </div>
        <form onSubmit={send} className="border-t border-[#0b0c0c] p-6 flex flex-col gap-3 bg-white">
          <label htmlFor="interrogation-input" className="form-label">Your question</label>
          <span className="form-hint -mt-2">Ask one clear question at a time.</span>
          <textarea
            id="interrogation-input" value={input} onChange={(e) => setInput(e.target.value)} rows={2} maxLength={600}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) send(e); }}
            placeholder={`Put a question to ${suspect.name}…`}
            className="form-input w-full resize-none"
            data-testid="interrogation-input"
          />
          <div className="flex items-center gap-4 mt-2">
            <button className="btn-spore" disabled={!input.trim() || !!pending} data-testid="interrogation-send-button"><Send size={18} /> Put question</button>
            <div className="ml-auto flex items-center gap-4">
              {!sus.progress.clue && <span className="text-[#505a5f] text-lg" data-testid="present-evidence-hint">Find this suspect's hidden clue in the asylum first</span>}
              {sus.progress.secret && <span className="text-[#00703c] font-bold text-lg" data-testid="secret-revealed-label">Exhibit presented · secret on file</span>}
              <button type="button" className="btn-ghost" disabled={!canPresent} onClick={() => run("present")} data-testid="present-evidence-button">
                <FileWarning size={18} /> {presented && !sus.progress.secret ? "Re-present evidence" : "Present evidence"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
